import { Dialog, useDialogRoute } from "@dash/dialog";
import { Dropdown } from "@dash/dropdown";
import { Button, TextField } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { useToast } from "@dash/toast";
import { useState } from "react";
import { MdMoreVert, MdOutlineEdit, MdOutlineLocalPrintshop, MdPublic } from "react-icons/md";
import { useQueryClient } from "react-query";

import { ThreadDto } from "@megaron/invoices-contracts";
import { useClientManager } from "@megaron/react-clients";

import { PrintViewContainer } from "./PrintView/PrintViewContainer";

type Props = {
  thread: ThreadDto;
  queryKey: string | string[];
};

export const HeaderBarSettings: React.FC<Props> = ({ thread, queryKey }) => {
  const queryClient = useQueryClient();
  const { isMobile } = useDeviceType();
  const toast = useToast();

  const [title, setTitle] = useState(thread.title);
  const [print, setPrint] = useState<{
    uuid: string | undefined;
    active: boolean;
  }>({ uuid: undefined, active: false });

  const renameThreadMutation = useClientManager("invoices").renameThread().useMutation();
  const setThreadPublicMutation = useClientManager("invoices").setThreadPublic().useMutation();
  const hasAnySignature = thread.budgetSignatures.length > 0 || thread.validitySignatures.length > 0;
  const canSetPublic = !thread.isPublic && !hasAnySignature;

  const onPrint = () => {
    setPrint({ active: true, uuid: thread.uuid });
  };

  const onPrintEnd = () => setPrint({ active: false, uuid: undefined });

  const renameDialog = useDialogRoute(`/zmien-nazwe`, ({ onClose }) => (
    <Dialog onClose={onClose} placement={isMobile ? "top" : "center"} header={"Zmień nazwę"} css={{ width: "400px" }}>
      {title && (
        <div css={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <TextField value={title} onChange={setTitle} autoFocus />
          <Button
            onClick={() => {
              renameThreadMutation.mutate(
                {
                  title: title,
                  threadUuid: thread.uuid,
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries(queryKey);
                    onClose();
                  },
                },
              );
            }}
          >
            Zapisz
          </Button>
        </div>
      )}
    </Dialog>
  ));

  const setPublicDialog = useDialogRoute(`/ustaw-publiczna`, ({ onClose }) => (
    <Dialog
      onClose={onClose}
      placement={isMobile ? "top" : "center"}
      header={"Ustaw jako publiczna"}
      css={{ width: "400px" }}
    >
      <div css={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <span>Czy na pewno chcesz ustawić tę fakturę jako publiczną?</span>
        <div css={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <Button
            onClick={() => {
              setThreadPublicMutation.mutate(
                { threadUuid: thread.uuid },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries(queryKey);
                    onClose();
                  },
                  onError: () => {
                    toast.error("Nie udało się zmienić wątku na publiczny");
                  },
                },
              );
            }}
          >
            Tak
          </Button>
          <Button onClick={onClose} variant="outline">
            Nie
          </Button>
        </div>
      </div>
    </Dialog>
  ));

  return (
    <>
      <Dropdown openButton={<MdMoreVert color="white" css={{ cursor: "pointer", fontSize: "24px" }} />}>
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 8px",
            cursor: "pointer",
          }}
          onClick={renameDialog.open}
        >
          <MdOutlineEdit />
          Zmień nazwę
        </div>
        {canSetPublic && (
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 8px",
              cursor: "pointer",
            }}
            onClick={setPublicDialog.open}
          >
            <MdPublic />
            Zmień na publiczny
          </div>
        )}
        {thread.status === "approved" && (
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 8px",
              cursor: "pointer",
            }}
            onClick={onPrint}
          >
            <MdOutlineLocalPrintshop />
            Drukuj
          </div>
        )}
      </Dropdown>
      {renameDialog.element}
      {setPublicDialog.element}
      {print.active && print.uuid && <PrintViewContainer threadUuid={thread.uuid} onPrintEnd={onPrintEnd} />}
    </>
  );
};
